import React, { useState, useRef, useEffect } from "react"
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
  Paper,
  alpha,
  useTheme,
} from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import FlightIcon from "@mui/icons-material/Flight"
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined"
import { httpsCallable } from "firebase/functions"
import { functions } from "../../../lib/firebase"

const QUICK_PROMPTS = [
  "How do I get from DFW to Ushuaia?",
  "What's the routing from PDX to Tokyo?",
  "Can I non-rev from DFW to Tikal?",
  "How does D2 standby priority work?",
  "Best time to non-rev to Europe from DFW?",
  "Routing from SLC for Mom?",
]

function Message({ role, content }) {
  const theme = useTheme()
  const isUser = role === "user"
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.5,
        gap: 1,
        alignItems: "flex-end",
      }}
    >
      {!isUser && (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mb: 0.25,
          }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 16, color: "primary.main" }} />
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          maxWidth: { xs: "88%", sm: "78%" },
          px: 2,
          py: 1.25,
          borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          bgcolor: isUser ? "primary.main" : "background.paper",
          color: isUser ? "primary.contrastText" : "text.primary",
          border: isUser ? "none" : "1px solid",
          borderColor: "divider",
          boxShadow: isUser
            ? `0 2px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            : "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65, fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
        >
          {content}
        </Typography>
      </Paper>
    </Box>
  )
}

export default function TravelChat() {
  const theme = useTheme()
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, loading])

  const sendMessage = async (text) => {
    const userMessage = text.trim()
    if (!userMessage || loading) return

    const newMessages = [...messages, { role: "user", content: userMessage }]
    setMessages(newMessages)
    setInput("")
    setLoading(true)

    try {
      const callAssistant = httpsCallable(functions, "travel_assistant")
      const result = await callAssistant({ messages: newMessages })
      setMessages([...newMessages, { role: "assistant", content: result.data.reply }])
    } catch (err) {
      console.error("travel_assistant callable failed:", err)
      setMessages([
        ...newMessages,
        { role: "assistant", content: "Sorry, something went wrong. Try again in a moment." },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#f8faff",
        borderRadius: "inherit",
        overflow: "hidden",
      }}
    >
      {/* Context badge */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          flexShrink: 0,
        }}
      >
        <FlightIcon fontSize="small" color="primary" />
        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: "0.78rem" }}>
          DFW Hub · AA Employee · D2 Full Benefits
        </Typography>
      </Box>

      {/* Message thread */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              gap: 2,
              px: 1,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SmartToyOutlinedIcon sx={{ fontSize: 28, color: "primary.main" }} />
            </Box>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 320 }}>
              Ask anything about AA non-rev travel — routing, standby costs, priority, or how to get anywhere.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "center",
                maxWidth: 640,
              }}
            >
              {QUICK_PROMPTS.map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  onClick={() => sendMessage(prompt)}
                  variant="outlined"
                  sx={{
                    cursor: "pointer",
                    minHeight: 36,
                    fontSize: "0.8rem",
                    color: "text.primary",
                    bgcolor: "background.paper",
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      borderColor: "primary.main",
                      color: "primary.main",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {messages.map((msg, i) => (
          <Message key={i} role={msg.role} content={msg.content} />
        ))}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5, gap: 1, alignItems: "flex-end" }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SmartToyOutlinedIcon sx={{ fontSize: 16, color: "primary.main" }} />
            </Box>
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: "18px 18px 18px 4px",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CircularProgress size={14} thickness={5} />
            </Paper>
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Input bar */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 1,
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about routes, standby, or how to get anywhere…"
          disabled={loading}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              minHeight: 44,
              backgroundColor: "#f8faff",
              fontSize: { xs: "0.875rem", sm: "0.9rem" },
            },
          }}
        />
        <IconButton
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || loading}
          color="primary"
          aria-label="Send message"
          sx={{
            minWidth: 44,
            minHeight: 44,
            bgcolor: input.trim() && !loading ? "primary.main" : "transparent",
            color: input.trim() && !loading ? "white" : "text.disabled",
            borderRadius: 2,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: input.trim() && !loading ? "primary.dark" : "transparent",
            },
            "&.Mui-disabled": { color: "text.disabled" },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  )
}
